import classnames from 'classnames/bind'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'

import {
  Accordion,
  AccordionPanel,
} from '@unlikelystudio/react-abstract-components'

import RichText, { RichTextBlock } from '~/components/UI/RichText'

import { useStyle } from '~/providers/StyleProvider'

import useTextColorFromTheme from '~/hooks/useTextColorFromTheme'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type AccordionItemProps = {
  headline?: string
  body?: RichTextBlock
}

export type AccordionThemes =
  | typeof GlobalThemeColors.Black
  | typeof GlobalThemeColors.White
  | typeof GlobalThemeColors.SilverChalice

export type AccordionProps = {
  className?: string
  headlineClassName?: string
  bodyClassName?: string
  items?: AccordionItemProps[]
  index?: number
  activeIcon?: JSX.Element | JSX.Element[]
  inactiveIcon?: JSX.Element | JSX.Element[]
  bordersTheme?: AccordionThemes
}

function AccordionItems({
  className,
  headlineClassName,
  bodyClassName,
  items,
  index,
  bordersTheme = GlobalThemeColors.SilverChalice,
  activeIcon,
  inactiveIcon,
}: AccordionProps) {
  const { textColor: color } = useTextColorFromTheme()

  const accordionStyle = useStyle({
    borderColor: bordersTheme ?? color,
  })

  const iconStyle = useStyle({
    textPreset: GlobalTextPreset.Cta10Grotesk,
  })

  return (
    <div className={cx(css.Accordion, className, accordionStyle)}>
      {items?.length > 0 && (
        <Accordion index={index}>
          {items?.map(({ headline, body }, key) => (
            <AccordionPanel
              className={cx(css.panel, accordionStyle)}
              key={`item_${key}`}
              index={key}
              head={(isActive: boolean) => (
                <div className={cx(css.panelHead, { isActive })}>
                  <div className={cx(css.headline, headlineClassName)}>
                    {headline}
                  </div>
                  <div className={cx(css.icon, iconStyle)}>
                    {((activeIcon && inactiveIcon && isActive) ||
                      (activeIcon && !inactiveIcon)) && <>{activeIcon}</>}
                    {inactiveIcon && !isActive && <>{inactiveIcon}</>}
                    {!activeIcon && !isActive && <div>{`(+)`}</div>}
                    {!activeIcon && isActive && <div>{`(–)`}</div>}
                  </div>
                </div>
              )}
              body={
                <div className={cx(css.panelBody)}>
                  <RichText
                    className={cx(css.text, bodyClassName)}
                    render={body}
                  />
                </div>
              }
            />
          ))}
        </Accordion>
      )}
    </div>
  )
}

AccordionItems.defaultProps = {
  index: 0,
}

export default AccordionItems
